/**
 * childPronouns.js
 * ----------------
 * Rewrites generic profile (HFP) question wording — written with "they",
 * "their" and "them" — in the child's own pronouns, with verb agreement:
 * "Move their focus … when they need to?" -> "Move her focus … when she
 * needs to?", "Do they go looking …?" -> "Does she go looking …?".
 *
 * Children whose pronouns are they/them, or not recorded, keep the wording
 * as written. Wording where "they" or "them" means someone or something
 * other than the child is listed in KEEP_AS_WRITTEN and never rewritten.
 *
 * Pages show only the reviewed wording (src/rms/constants/
 * hfpV3PronounWording.json, every sentence read by hand): personaliseWording
 * looks a sentence up there and shows anything not in it exactly as written,
 * so the rules never reach a family unreviewed. applyChildPronouns is the
 * rule set that drafts that table when the questions change; a test proves
 * the table and the rules agree.
 */
import REVIEWED_TABLE from '../constants/hfpV3PronounWording.json';

const FORMS = {
  she_her: { subj: 'she', obj: 'her', pos: 'her', posAbs: 'hers', refl: 'herself' },
  he_him: { subj: 'he', obj: 'him', pos: 'his', posAbs: 'his', refl: 'himself' },
};

// Wording where "they"/"them" refers to someone or something other than the
// child, and nothing refers to the child. Matched exactly; shown as written.
export const KEEP_AS_WRITTEN = new Set([
  'Work out how long tasks will take and do them in the right order?',
  'Read words and understand what they mean?',
  'Understand what people say and what they mean, including jokes, hints, and when words do not mean what they say?',
  'Understand what someone means when they move their hands, face or body?',
  'Sniff or smell things to find out more about them?',
  'Work out how far away things are, and where they are?',
]);

// Wording the rules cannot get right on their own — "them" meaning something
// other than the child in a sentence that also names the child, or lists of
// verbs that all need agreement. Reviewed by hand for each pronoun set.
export const REVIEWED_WORDING = {
  'Copy what they see or hear - like how someone moves, a sound, or a letter?': {
    she_her: 'Copy what she sees or hears - like how someone moves, a sound, or a letter?',
    he_him: 'Copy what he sees or hears - like how someone moves, a sound, or a letter?',
  },
  'Bring back details they have learned in the past and think about them when they need to?': {
    she_her: 'Bring back details she has learned in the past and think about them when she needs to?',
    he_him: 'Bring back details he has learned in the past and think about them when he needs to?',
  },
  'Compare different choices and weigh them up to decide what they think?': {
    she_her: 'Compare different choices and weigh them up to decide what she thinks?',
    he_him: 'Compare different choices and weigh them up to decide what he thinks?',
  },
  'Say what they think, feel, or want to share?': {
    she_her: 'Say what she thinks, feels, or wants to share?',
    he_him: 'Say what he thinks, feels, or wants to share?',
  },
  'Use writing to share what they think, feel, or want to tell others?': {
    she_her: 'Use writing to share what she thinks, feels, or wants to tell others?',
    he_him: 'Use writing to share what he thinks, feels, or wants to tell others?',
  },
  'Control how strong their feelings are and how they show them?': {
    she_her: 'Control how strong her feelings are and how she shows them?',
    he_him: 'Control how strong his feelings are and how he shows them?',
  },
  'Learn new skills and keep going until they can do them like using tools, playing games, or learning new ways to do things?': {
    she_her: 'Learn new skills and keep going until she can do them like using tools, playing games, or learning new ways to do things?',
    he_him: 'Learn new skills and keep going until he can do them like using tools, playing games, or learning new ways to do things?',
  },
};

const MODALS = new Set(['can', 'could', 'will', 'would', 'should', 'might', 'may', 'must']);
const ADVERBS = new Set(['usually', 'already', 'often', 'sometimes', 'always', 'never', 'really', 'also', 'still', 'just', 'easily']);
const IRREGULAR = { are: 'is', have: 'has', do: 'does', go: 'goes', were: 'was' };

/** Third person singular of a base-form verb ("need" -> "needs"). */
export function thirdPerson(verb) {
  const lower = verb.toLowerCase();
  let out;
  if (IRREGULAR[lower]) out = IRREGULAR[lower];
  else if (/(s|sh|ch|x|z|o)$/.test(lower)) out = `${lower}es`;
  else if (/[^aeiou]y$/.test(lower)) out = `${lower.slice(0, -1)}ies`;
  else out = `${lower}s`;
  return verb[0] === verb[0].toUpperCase() ? out[0].toUpperCase() + out.slice(1) : out;
}

function cap(word, like) {
  return like[0] === like[0].toUpperCase() ? word[0].toUpperCase() + word.slice(1) : word;
}

/**
 * @param {string} text      generic wording
 * @param {string} pronouns  'she_her' | 'he_him' | 'they_them' | null
 * @returns {string}
 */
export function applyChildPronouns(text, pronouns) {
  const f = FORMS[pronouns];
  if (!f || !text || KEEP_AS_WRITTEN.has(text)) return text;
  const reviewed = REVIEWED_WORDING[text];
  if (reviewed && reviewed[pronouns]) return reviewed[pronouns];
  let s = String(text);

  // Questions led by an auxiliary: "Do they" -> "Does she", "Are they" -> "Is she".
  s = s.replace(/\b(Do|do|Are|are|Have|have)\s+they\b/g, (m, aux) => {
    const map = { do: 'does', are: 'is', have: 'has' };
    return `${cap(map[aux.toLowerCase()], aux)} ${f.subj}`;
  });

  // Contractions, with straight or curly apostrophes.
  s = s.replace(/\b(T|t)hey(['\u2019])re\b/g, (m, t, a) => `${cap(f.subj, t)}${a}s`);
  s = s.replace(/\b(T|t)hey(['\u2019])ve\b/g, (m, t, a) => `${cap(f.subj, t)}${a}s`);
  s = s.replace(/\b(T|t)hey don(['\u2019])t\b/g, (m, t, a) => `${cap(f.subj, t)} doesn${a}t`);

  // "they" + optional adverb + verb: agree the verb unless it is a modal or
  // already agreed (handled above).
  s = s.replace(/\b(They|they)\s+(\w+)(\s+(\w+))?/g, (m, they, w1, rest, w2) => {
    const subj = cap(f.subj, they);
    const lower = w1.toLowerCase();
    if (MODALS.has(lower)) return `${subj} ${w1}${rest || ''}`;
    if (ADVERBS.has(lower) && w2) return `${subj} ${w1} ${thirdPerson(w2)}`;
    return `${subj} ${thirdPerson(w1)}${rest || ''}`;
  });

  s = s.replace(/\bthemselves\b/g, f.refl).replace(/\bThemselves\b/g, cap(f.refl, 'T'));
  s = s.replace(/\btheirs\b/g, f.posAbs);
  s = s.replace(/\b(T|t)heir\b/g, (m, t) => cap(f.pos, t));
  s = s.replace(/\bthem\b/g, f.obj);
  return s;
}

const REVIEWED_LOOKUP = new Map(REVIEWED_TABLE.map((row) => [row.text, row]));

/**
 * The reviewed wording for a sentence in the child's pronouns, or the
 * sentence exactly as written when it has not been reviewed.
 */
export function personaliseWording(text, pronouns) {
  if (!text || !FORMS[pronouns]) return text;
  const row = REVIEWED_LOOKUP.get(text);
  return (row && row[pronouns]) || text;
}

const WORDING_FIELDS = ['label', 'description', 'title', 'helpText'];

/**
 * A copy of a form schema with its wording (question labels, option labels,
 * hints and section titles) in the child's pronouns, from the reviewed table.
 * Ids, values and structure are untouched, so answers and validation are
 * unaffected. Returns the schema itself when there is nothing to change.
 */
export function personaliseSchemaWording(schema, pronouns) {
  if (!schema || !FORMS[pronouns]) return schema;
  const walk = (node) => {
    if (Array.isArray(node)) return node.map(walk);
    if (!node || typeof node !== 'object') return node;
    const out = {};
    Object.keys(node).forEach((key) => {
      const value = node[key];
      out[key] = WORDING_FIELDS.includes(key) && typeof value === 'string'
        ? personaliseWording(value, pronouns)
        : walk(value);
    });
    return out;
  };
  return walk(schema);
}
